// Step catalogue, defaults, summaries and validation for the Flow editor.
// Validation mirrors backend/services/flow-core.js (the server re-checks everything).

export const RANK = { start: 0, content: 1, step: 2, postTypes: 3, approval: 4, postToChannel: 5, after: 6 };

export const TYPES = {
  schedule:      { kind: "start",         icon: "⏰", label: "Schedule",        help: "Posts at set times through the day" },
  newPost:       { kind: "start",         icon: "⚡", label: "New post",        help: "Posts whenever a source feed does" },
  aiTopics:      { kind: "content",       icon: "✨", label: "AI topics",       help: "AI writes posts from your topics" },
  channelPosts:  { kind: "content",       icon: "📢", label: "Channel posts",   help: "Takes new posts from other channels" },
  rss:           { kind: "content",       icon: "🌐", label: "RSS feed",        help: "Takes new articles from website feeds" },
  filter:        { kind: "step",          icon: "🔎", label: "Filter",          help: "Keeps only posts with the words you pick" },
  aiRewrite:     { kind: "step",          icon: "✍️", label: "AI rewrite",      help: "Rewrites taken posts in your channel's voice" },
  editText:      { kind: "step",          icon: "📝", label: "Edit text",       help: "Adds your lines, sets link, tags, removes links" },
  postTypes:     { kind: "postTypes",     icon: "🖼️", label: "Post types",      help: "Text, image or video" },
  approval:      { kind: "approval",      icon: "🛡️", label: "Approval",        help: "Approve each post, or publish on its own" },
  postToChannel: { kind: "postToChannel", icon: "➤",  label: "Post to channel", help: "A channel you own" },
};

export const typeOf = (t) => TYPES[t] || { kind: "unknown", icon: "❔", label: String(t), help: "This step is not supported." };

export const PALETTE = [
  { title: "Start",   types: ["schedule", "newPost"] },
  { title: "Content", types: ["aiTopics", "channelPosts", "rss"] },
  { title: "Steps",   types: ["filter", "aiRewrite", "editText", "postTypes", "approval"] },
  { title: "End",     types: ["postToChannel"] },
];

let counter = 0;
export const uid = () => `n${Date.now().toString(36)}${(counter++).toString(36)}`;

export const defaults = (type) => {
  switch (type) {
    case "schedule": return { postsPerDay: 2, maxPerWeek: 14, windowStart: 9, windowEnd: 21, days: [0, 1, 2, 3, 4, 5, 6], skipDates: [], tzOffsetMin: -new Date().getTimezoneOffset() };
    case "newPost": return { checkEveryMin: 30 };
    case "aiTopics": return { topics: [], extraInstructions: "" };
    case "channelPosts": return { channelJids: [], rightsConfirmed: false };
    case "rss": return { feeds: [] };
    case "filter": return { include: [], exclude: [] };
    case "editText": return { prepend: "", append: "", linkMode: "keep", setLink: "", tags: [] };
    case "postTypes": return { text: true, image: false, video: false };
    case "approval": return { mode: "approve", notifyWhatsApp: false, minScore: 80 };
    case "postToChannel": return { instanceId: "", channelId: "" };
    default: return {};
  }
};

export const makeNode = (type) => ({ id: uid(), type, config: defaults(type) });

export const newFlowNodes = () => ["schedule", "aiTopics", "postTypes", "approval", "postToChannel"].map(makeNode);

// Insert keeping the fixed order: after the last node whose rank is <= the new one.
export const insertNode = (nodes, type) => {
  const rank = RANK[typeOf(type).kind];
  let at = 0;
  nodes.forEach((n, i) => { if ((RANK[typeOf(n.type).kind] ?? 99) <= rank) at = i + 1; });
  const next = nodes.slice();
  next.splice(at, 0, makeNode(type));
  return next;
};

// A palette item is disabled when the flow already has one of that step.
export const isDisabled = (nodes, type) => {
  if (TYPES[type].soon) return true;
  const kind = TYPES[type].kind;
  if (["start", "content", "postTypes", "approval", "postToChannel"].includes(kind)) return nodes.some((n) => TYPES[n.type].kind === kind);
  return nodes.some((n) => n.type === type);
};

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun as in the reference
export const dayName = (d) => DAY_NAMES[d];

const hhmm = (mins) => `${String(Math.floor(mins / 60) % 24).padStart(2, "0")}:${String(Math.round(mins % 60)).padStart(2, "0")}`;
export const slotLabels = (c) => {
  const n = Math.max(1, Math.min(50, c.postsPerDay | 0 || 1));
  const ws = c.windowStart * 60, we = c.windowEnd * 60;
  if (!(we > ws)) return [];
  return Array.from({ length: n }, (_, k) => hhmm(Math.round(ws + ((we - ws) / n) * k)));
};

const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };

export const summary = (node, channels = []) => {
  const c = node.config || {};
  if (!TYPES[node.type]) return "Not supported";
  switch (node.type) {
    case "schedule": {
      const days = (c.days || []).length === 7 ? "Every day" : (c.days || []).length ? DAY_ORDER.filter((d) => c.days.includes(d)).map(dayName).join(" ") : "No days";
      return `${c.postsPerDay} a day${c.maxPerWeek ? ` · max ${c.maxPerWeek}/week` : ""} · ${hhmm(c.windowStart * 60)}–${hhmm((c.windowEnd % 24) * 60)} · ${days}`;
    }
    case "newPost": return `Checks every ${c.checkEveryMin} min`;
    case "aiTopics": return (c.topics || []).length ? `${c.topics.length} topic${c.topics.length > 1 ? "s" : ""}` : "No topics yet";
    case "channelPosts": return (c.channelJids || []).length ? `${c.channelJids.length} channel${c.channelJids.length > 1 ? "s" : ""}` : "No channels yet";
    case "rss": return (c.feeds || []).length ? (c.feeds.length === 1 ? host(c.feeds[0]) : `${c.feeds.length} feeds`) : "No feeds yet";
    case "filter": return [(c.include || []).length ? `Keep: ${c.include.join(", ")}` : "", (c.exclude || []).length ? `Skip: ${c.exclude.join(", ")}` : ""].filter(Boolean).join(" · ") || "No words yet";
    case "aiRewrite": return "In your channel's voice";
    case "editText": return [c.prepend && "Adds a first line", c.append && "Adds a last line", c.linkMode === "remove" && "Removes links", c.linkMode === "set" && "Sets link", (c.tags || []).length && "Adds tags"].filter(Boolean).join(" · ") || "No changes yet";
    case "postTypes": return [c.text && "Text", c.image && "Image"].filter(Boolean).join(", ") || "None chosen";
    case "approval": return c.mode === "auto" ? "Publishes on its own" : "You approve each post";
    case "postToChannel": { const ch = channels.find((x) => x._id === c.channelId); return ch ? ch.name : "Choose a channel"; }
    default: return "";
  }
};

const isHttp = (u) => { try { return ["http:", "https:"].includes(new URL(String(u)).protocol); } catch { return false; } };

// Returns [{ nodeId, message }]
export const validate = (nodes) => {
  const issues = [];
  const add = (n, message) => issues.push({ nodeId: n ? n.id : "", message });
  const count = (k) => nodes.filter((n) => TYPES[n.type]?.kind === k).length;
  nodes.filter((n) => !TYPES[n.type]).forEach((n) => add(n, `"${n.type}" is not supported yet. Delete this step.`));

  if (count("start") !== 1) add(null, count("start") ? "Use only one start step." : "Add a start step: Schedule or New post.");
  if (count("content") !== 1) add(null, count("content") ? "Use only one content step." : "Add a content step: AI topics or RSS feed.");
  if (count("postTypes") !== 1) add(null, "Add one Post types step.");
  if (count("approval") !== 1) add(null, "Add one Approval step.");
  if (count("postToChannel") !== 1) add(null, "Add one Post to channel step.");

  const start = nodes.find((n) => TYPES[n.type]?.kind === "start");
  const content = nodes.find((n) => TYPES[n.type]?.kind === "content");
  if (start?.type === "newPost" && content?.type === "aiTopics") add(start, "New post needs a feed to watch, not AI topics.");

  nodes.forEach((n) => {
    const c = n.config || {};
    if (n.type === "schedule") {
      if (!(c.postsPerDay >= 1 && c.postsPerDay <= 50)) add(n, "Posts per day must be between 1 and 50.");
      if (!(c.windowEnd > c.windowStart)) add(n, "Active hours must end after they start.");
      if (!(c.days || []).length) add(n, "Pick at least one day.");
    }
    if (n.type === "aiTopics" && !(c.topics || []).length) add(n, "Add at least one topic.");
    if (n.type === "rss") {
      if (!(c.feeds || []).length) add(n, "Add at least one feed address.");
      else if (c.feeds.some((f) => !isHttp(f))) add(n, "Every feed address must start with http:// or https://");
    }
    if (n.type === "channelPosts") {
      if (!(c.channelJids || []).length) add(n, "Choose at least one channel to read from.");
      else if (!c.rightsConfirmed) add(n, "Confirm you have the right to reuse these posts.");
    }
    if (n.type === "afterPostDone" || n.type === "shareToGroups") add(n, "This step is coming soon.");
    if (n.type === "filter" && !(c.include || []).length && !(c.exclude || []).length) add(n, "Add words to keep or to leave out.");
    if (n.type === "editText" && c.linkMode === "set" && !isHttp(c.setLink)) add(n, "Enter the link to set (http:// or https://).");
    if (n.type === "postTypes" && !c.text && !c.image) add(n, "Choose at least one post type.");
    if (n.type === "postToChannel" && !c.channelId) add(n, "Choose a channel you own.");
  });

  for (let i = 1; i < nodes.length; i++) {
    if (RANK[TYPES[nodes[i].type]?.kind] < RANK[TYPES[nodes[i - 1].type]?.kind]) /* unknown types are reported separately */ { add(nodes[i], "This step is out of order."); break; }
  }
  return issues;
};
